import React, { useState, useEffect } from 'react';
import { MediaService } from '../../../Services/Axios/Api';
import { Container, Row, Col, Button, Spinner, Card, Modal, Form, InputGroup, Pagination, Dropdown, Tabs, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faCopy, faFilter, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Captions from "yet-another-react-lightbox/plugins/captions";
import UploadMediaDialog from './UploadMediaDialog';
import './Media.css';
import MediaGalleryFoldersAdmin from './MediaGalleryFolders';
import { isVideoFile, isPdfFile, getMediaTypeFromPath } from './mediaFileUtils';

const Media = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(-1);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedImageId, setCopiedImageId] = useState(null);
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');

  const ITEMS_PER_PAGE = 12;

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await MediaService.getMedia();
      setMedia(response.result.reverse() || []);
      setCurrentPage(1);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching media:', error);
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    setUploadDialogOpen(false);
    fetchMedia();
  };

  const handleDeleteClick = (e, image) => {
    e.stopPropagation(); // Prevent lightbox from opening
    setSelectedImage(image);
    setDeleteDialogOpen(true);
  };

  const handleCopyClick = async (e, image) => {
    e.stopPropagation();

    try {
      if (!navigator?.clipboard?.writeText) {
        console.warn('Clipboard API not supported in this browser.');
        return;
      }

      await navigator.clipboard.writeText(image.filePath);
      setCopiedImageId(image.id);
      setTimeout(() => setCopiedImageId(null), 2000);
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
    setIndex(-1);
  };

  const handleFilterChange = (value) => {
    if (!value) {
      return;
    }

    setMediaTypeFilter(value);
    setCurrentPage(1);
    setIndex(-1);
  };

  const filteredMedia = media.filter((item) => {
    const altText = (item.altText || '').toLowerCase();
    const filePath = (item.filePath || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    const matchesSearch = altText.includes(query) || filePath.includes(query);
    const mediaType = getMediaTypeFromPath(item.filePath || '');

    const matchesType =
      mediaTypeFilter === 'all' ||
      mediaTypeFilter === mediaType;

    return matchesSearch && matchesType;
  });

  const lightboxMedia = filteredMedia.filter((item) => !isPdfFile(item.filePath));

  const totalPages = Math.max(Math.ceil(filteredMedia.length / ITEMS_PER_PAGE), 1);
  const indexOfFirstItem = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentMedia = filteredMedia.slice(indexOfFirstItem, indexOfFirstItem + ITEMS_PER_PAGE);

  const handleDeleteConfirm = async () => {
    try {
      await MediaService.deleteMedia(selectedImage.id);
      setDeleteDialogOpen(false);
      fetchMedia();
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const slides = lightboxMedia.map(item => ({
    src: item.filePath,
    title: item.altText,
    description: item.altText,
    type: isVideoFile(item.filePath) ? 'video' : 'image'
  }));

  const handleMediaClick = (item) => {
    if (isPdfFile(item.filePath)) {
      window.open(item.filePath, '_blank', 'noopener,noreferrer');
      return;
    }
    const slideIndex = lightboxMedia.findIndex((m) => m.id === item.id);
    if (slideIndex >= 0) setIndex(slideIndex);
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-80">
        <Spinner animation="border" variant="primary" />
      </Container>
    );  
  }

  return (
    <Container className="py-4">
      <Tabs defaultActiveKey="library" id="media-tabs" className="mb-3">
        <Tab eventKey="library" title="Media Library">
          <Row className="mb-4 align-items-center mt-3">
            <Col xs={12} md={6} className="mb-2 mb-md-0">
              <h2 className="mb-0">Media Library</h2>
            </Col>
            <Col xs={12} md={6}>
              <InputGroup>
                <Form.Control
                  type="search"
                  placeholder="Search media..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <Dropdown align="end" onSelect={handleFilterChange}>
                  <Dropdown.Toggle variant="outline-secondary" id="media-filter-toggle">
                    <FontAwesomeIcon icon={faFilter} className="me-2" />
                    {mediaTypeFilter === 'all' && 'All'}
                    {mediaTypeFilter === 'image' && 'Images'}
                    {mediaTypeFilter === 'video' && 'Videos'}
                    {mediaTypeFilter === 'pdf' && 'PDFs'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item eventKey="all" active={mediaTypeFilter === 'all'}>
                      All
                    </Dropdown.Item>
                    <Dropdown.Item eventKey="image" active={mediaTypeFilter === 'image'}>
                      Images
                    </Dropdown.Item>
                    <Dropdown.Item eventKey="video" active={mediaTypeFilter === 'video'}>
                      Videos
                    </Dropdown.Item>
                    <Dropdown.Item eventKey="pdf" active={mediaTypeFilter === 'pdf'}>
                      PDFs
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </InputGroup>
            </Col>
          </Row>
          
          <Row className="g-4">
            {currentMedia.map((item) => {
              const fileName = (item.filePath || '').split('/').pop() || 'document.pdf';
              return (
                <Col key={item.id} xs={12} sm={6} md={4} lg={3}>
                  <Card className="h-100 media-card" onClick={() => handleMediaClick(item)}>
                    <div className="media-image-container">
                      {isPdfFile(item.filePath) ? (
                        <div className="media-pdf-placeholder">
                          <FontAwesomeIcon icon={faFilePdf} className="media-pdf-icon" />
                          <span className="media-pdf-filename">{fileName}</span>
                        </div>
                      ) : isVideoFile(item.filePath) ? (
                        <video
                          controls
                          preload="metadata"
                          src={item.filePath}
                          className="media-image"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <Card.Img 
                          variant="top" 
                          src={item.filePath} 
                          alt={item.altText}
                          className="media-image"
                          loading="lazy"
                        />
                      )}
                      <div className="media-overlay">
                        <p className="mb-0 text-white">{item.altText}</p>
                      </div>
                      {copiedImageId === item.id && (
                        <div className="copy-feedback">Link copied!</div>
                      )}
                      <Button
                        variant="secondary"
                        className="copy-button"
                        onClick={(e) => handleCopyClick(e, item)}
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </Button>
                      <Button
                        variant="danger"
                        className="delete-button"
                        onClick={(e) => handleDeleteClick(e, item)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {filteredMedia.length === 0 && (
            <Row className="mt-4">
              <Col>
                <p className="text-center text-muted">No media found.</p>
              </Col>
            </Row>
          )}

          {filteredMedia.length > ITEMS_PER_PAGE && (
            <Row className="mt-4">
              <Col>
                <Pagination className="justify-content-center">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                  {Array.from({ length: totalPages }, (_, pageIndex) => {
                    const pageNumber = pageIndex + 1;
                    return (
                      <Pagination.Item
                        key={pageNumber}
                        active={pageNumber === currentPage}
                        onClick={() => setCurrentPage(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    );
                  })}
                  <Pagination.Next
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
              </Col>
            </Row>
          )}

          <Button 
            variant="primary" 
            className="position-fixed bottom-0 end-0 m-4 rounded-circle"
            style={{ width: '56px', height: '56px' }}
            onClick={() => setUploadDialogOpen(true)}
          >
            <FontAwesomeIcon icon={faPlus} size="lg" />
          </Button>

          <Lightbox
            slides={slides}
            open={index >= 0}
            index={index}
            close={() => setIndex(-1)}
            plugins={[Thumbnails, Zoom, Fullscreen, Slideshow, Captions]}
            render={{
              slide: ({ slide }) =>
                slide.type === 'video' ? (
                  <video controls preload="metadata" src={slide.src} style={{ width: '100%', height: 'auto' }} />
                ) : (
                  <img src={slide.src} alt={slide.title} style={{ width: '100%', height: 'auto' }} loading="lazy" />
                ),
            }}
            carousel={{
              finite: true
            }}
            thumbnails={{
              position: 'bottom',
              width: 120,
              height: 80,
              border: 0,
              borderRadius: 4,
              padding: 4,
              gap: 16,
              showToggle: true
            }}
            zoom={{
              maxZoomPixelRatio: 2,
              zoomInMultiplier: 2,
              doubleTapDelay: 300,
              doubleClickDelay: 300,
              doubleClickMaxStops: 2,
              keyboardMoveDistance: 50,
              wheelZoomDistanceFactor: 100,
              pinchZoomDistanceFactor: 100,
              scrollToZoom: false
            }}
            styles={{ 
              container: { backgroundColor: "rgba(0, 0, 0, .9)" },
              root: { "--yarl__color_backdrop": "rgba(0, 0, 0, .9)" }
            }}
          />

          <UploadMediaDialog
            open={uploadDialogOpen}
            onClose={() => setUploadDialogOpen(false)}
            onSuccess={handleUploadSuccess}
          />

          <Modal show={deleteDialogOpen} onHide={() => setDeleteDialogOpen(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Delete Media</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to delete this file? This action cannot be undone.
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>
        </Tab>
        <Tab eventKey="folders" title="Media Folders">
          <MediaGalleryFoldersAdmin />
        </Tab>
      </Tabs>
    </Container>
  );
};

export default Media;
